import { SxProps, Theme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion, Variants } from 'framer-motion';
import { Children, ReactNode } from 'react';
import { STAGGER_STEP_MS } from '../../constants/motion.constant';
import { toSeconds, useMotion } from './motion-provider';

const RISE_PX = 6;

/**
 * Wrapping the motion div with MUI `styled` is what makes `sx` work on it,
 * so the container can be the layout element (a grid, usually) and the
 * `StaggerItem`s can be its direct children. Nesting a grid inside instead
 * would leave the list with a single child and no stagger at all.
 */
const StaggerContainer = styled(motion.div)({});

interface StaggerListProps {
  children: ReactNode;
  testId?: string;
  /** Layout for the container itself; its children are the staggered items. */
  sx?: SxProps<Theme>;
}

/**
 * Container whose `StaggerItem` children appear one after another. The step
 * shrinks as the list grows so the whole group never takes longer than
 * `durations.long`.
 */
export const StaggerList = ({
  children,
  testId,
  sx,
}: StaggerListProps): JSX.Element => {
  const { durations, prefersReducedMotion } = useMotion();
  const count = Math.max(Children.count(children), 1);
  const stepMs = Math.min(STAGGER_STEP_MS, durations.long / count);

  const variants: Variants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : toSeconds(stepMs),
      },
    },
  };

  return (
    <StaggerContainer
      data-testid={testId}
      data-reduced-motion={prefersReducedMotion}
      data-stagger-step-ms={stepMs}
      sx={sx}
      variants={variants}
      initial="hidden"
      animate="show"
    >
      {children}
    </StaggerContainer>
  );
};

interface StaggerItemProps {
  children: ReactNode;
  testId?: string;
}

export const StaggerItem = ({
  children,
  testId,
}: StaggerItemProps): JSX.Element => {
  const { durations, prefersReducedMotion } = useMotion();
  const rise = prefersReducedMotion ? 0 : RISE_PX;

  const variants: Variants = {
    hidden: { opacity: 0, y: rise },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: toSeconds(durations.short), ease: 'easeOut' },
    },
  };

  return (
    <motion.div
      data-testid={testId}
      data-reduced-motion={prefersReducedMotion}
      variants={variants}
    >
      {children}
    </motion.div>
  );
};
